export interface S3UploadResult {
  key: string
  originalName: string
  contentType: string
  size: number
}

/**
 * Upload a file directly to S3 using a presigned URL
 * @param file - File to upload
 * @param userId - User ID for organizing files
 * @param serverUrl - Server URL to request presigned URL from
 * @param token - Supabase JWT token for authentication
 * @returns Upload result with S3 key
 */
export async function uploadFileToS3(
  file: File,
  userId: string,
  serverUrl: string,
  token: string,
): Promise<S3UploadResult> {
  try {
    // Step 1: Request presigned URL from backend
    const presignedResponse = await fetch(`${serverUrl}/generate-upload-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
      }),
      credentials: "include", // Include cookies for authentication
    })

    if (!presignedResponse.ok) {
      throw new Error(`Failed to get upload URL: ${presignedResponse.statusText}`)
    }

    const { uploadUrl, key } = await presignedResponse.json()

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to S3: ${uploadResponse.statusText}`)
    }

    return {
      key,
      originalName: file.name,
      contentType: file.type || "application/octet-stream",
      size: file.size,
    }
  } catch (error) {
    console.error(`[S3 Upload] Failed to upload ${file.name}:`, error)
    throw new Error(`Failed to upload ${file.name} to S3: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Upload multiple files to S3 in parallel using presigned URLs
 * @param files - Array of files to upload
 * @param userId - User ID for organizing files
 * @param serverUrl - Server URL to request presigned URLs from
 * @param token - Supabase JWT token for authentication
 * @returns Array of upload results
 */
export async function uploadFilesToS3(
  files: File[],
  userId: string,
  serverUrl: string,
  token: string,
): Promise<S3UploadResult[]> {
  const uploadPromises = files.map((file) => uploadFileToS3(file, userId, serverUrl, token))
  return Promise.all(uploadPromises)
}
